export const caseStudies = [
  {
    slug: "version3-omni-channel",
    title: "Version3 Omni-Channel",
    tagline: "Five apps, five logins — one inbox.",
    tagPill: "Own Product · Flagship",
    cardImage: "/work/version3-omni-channel/hero.html",
    heroImage: "/work/version3-omni-channel/hero.html",
    thumbnail: "/work/version3-omni-channel/hero.html",
    year: "2026",
    timeline: "Jan 2026 – ongoing",
    services: ["SaaS Platform", "Customer Messaging"],
    industry: "Retail & F-commerce · D2C · Customer Support",
    client: "Version3 (own product)",
    status: "Live",
    liveUrl: "https://omnichannel.version3.ai",
    challenge:
      "A business selling across three Facebook Pages, two Instagram accounts and a WhatsApp number runs five apps with five logins, and nobody knows who already replied. Messages get missed or answered twice, and owners have no view of response times or who is actually on shift.",
    solution:
      "Brings Facebook, Instagram and WhatsApp messages and comments, plus WooCommerce and Shopify store conversations, into one real-time team inbox, with shift management, workload routing, performance analytics and payroll built in. It works with ordinary personal WhatsApp numbers via QR pairing, not just the Business API — most small businesses never complete Meta verification, so competitors simply cannot serve them.",
    results:
      "Five separate channels unified into one inbox. Owners went from no visibility at all to live response-time, resolution and workload metrics. Payroll generated automatically from real clocked hours instead of by hand.",
    galleryImages: [
      "/work/version3-omni-channel/gallery-2.html",
      "/work/version3-omni-channel/gallery-1.html",
    ],
    galleryCaptions: [
      "Team performance: response times per moderator, the hours customers actually write in, and payroll built from clocked duty hours.",
      "Connecting an ordinary WhatsApp number by QR code, with no Business API and no Meta verification.",
    ],
    stats: [
      { number: "5+", label: "Apps Unified", description: "Five separate apps and logins (Facebook, Instagram, WhatsApp, WooCommerce and Shopify) combined into one inbox." },
      { number: "QR Pairing", label: "WhatsApp, No Business API", description: "Works with ordinary personal WhatsApp numbers, not just Meta-verified Business accounts." },
      { number: "Automatic", label: "Payroll", description: "Generated directly from clocked hours and resolutions — no manual calculation." },
    ],
    relatedSlugs: ["version3-ai-support-agent", "version3-website-hiring-suite"],
  },
  {
    slug: "errum-online-store",
    title: "ERRUM Online Store",
    tagline: "A premium sneaker drop store, built end to end.",
    tagPill: "Client Project",
    cardImage: "/work/errum-online-store/hero.html",
    heroImage: "/work/errum-online-store/hero.html",
    thumbnail: "/work/errum-online-store/hero.html",
    year: "2026",
    timeline: "~10 days build (Jul 2026), plus post-launch optimisation",
    services: ["E-commerce Storefront", "Admin Platform"],
    industry: "Fashion & Footwear Retail · Sneaker/Streetwear Resale",
    client: "ERRUM (ErrumBD) — premium sneaker & streetwear reseller, Bangladesh",
    status: "Live",
    liveUrl: "https://www.errumbd.com",
    challenge:
      "ERRUM was selling premium sneakers and streetwear entirely through social channels, with no store of its own — no catalogue customers could browse, no size-level stock visibility, no checkout, and no back office for managing fast-moving reseller inventory.",
    solution:
      "A complete e-commerce platform for a premium sneaker and streetwear reseller — editorial storefront, size-level catalogue, cart, wishlist and checkout, plus a full admin dashboard for products and orders. It handles a 5,600-entry catalogue where every shoe carries its own sizes, prices and stock levels — and still loads fast on a phone.",
    results:
      "Full storefront and admin platform delivered in ten days. Site-wide broken product images eliminated. Hosting costs brought back under control after launch, so the store runs sustainably at its full catalogue size.",
    galleryImages: [
      "/work/errum-online-store/gallery-1.html",
      "/work/errum-online-store/gallery-2.html",
    ],
    galleryCaptions: [
      "The product page on a phone: every size carries its own price and stock, then checkout takes a Tk 300 advance and cash on delivery.",
      "The admin board: a new order lands live, staff confirm it in one click and the stock for that size updates.",
    ],
    // home card: the live errumbd.com hero, rebuilt as a motion screen
    cardScreens: ["/work/errum-online-store/card-hero.html"],
    stats: [
      { number: "5,600+", label: "Catalogue Items", description: "Every shoe listed with its own per-size pricing and stock levels." },
      { number: "200+", label: "Daily Orders", description: "High volume of daily orders handled efficiently." },
      { number: "Zero", label: "Broken Product Images", description: "Site-wide broken image issue eliminated post-launch." },
    ],
    relatedSlugs: ["rahiee-ai", "listenr-for-bkash"],
  },
  {
    slug: "version3-ai-support-agent",
    title: "Version3 AI Support Agent",
    tagline: "An AI agent that sells, tracks orders, and knows when to stop.",
    tagPill: "Own Product · Platform Module",
    cardImage: "/work/version3-ai-support-agent/hero.html",
    heroImage: "/work/version3-ai-support-agent/hero.html",
    thumbnail: "/work/version3-ai-support-agent/hero.html",
    year: "2026",
    timeline: "Feb 2026 – Aug 2026 (~6 months)",
    services: ["AI Customer Support Agent"],
    industry: "E-commerce · Customer Support · Retail Automation",
    client: "Version3 (own product) — sold with the Omni-Channel platform",
    status: "Live — in integration with Omni-Channel",
    liveUrl: null,
    challenge:
      "Businesses answer the same questions hundreds of times a day — is this in stock, what sizes do you have, where is my order, what is your return policy — and every one of them ties up a human moderator. Ordinary chatbots cannot reach into a real product catalogue, so they guess, and they have no safe way to hand a conversation back before doing damage.",
    solution:
      "An AI customer support agent that answers product questions, places orders, tracks deliveries and hands over to a human when it should — in English, Bangla or Banglish. It is not a public chatbot anyone can open: each business gets its own agent, set up on its own Facebook, Instagram and WhatsApp channels and connected to its own store. Built as the automation layer for the Omni-Channel platform, it is configurable per business — skills and tone are set per client, no rebuild needed — so onboarding a new client is a setup task, not a development project.",
    results:
      "First-line support automated across product discovery, ordering and order tracking. New clients onboard by configuration instead of custom development. Works with WooCommerce, Shopify or a custom store through one connection. Cost per conversation is measurable rather than an unpredictable AI bill.",
    galleryImages: [
      "/work/version3-ai-support-agent/gallery-1.html",
      "/work/version3-ai-support-agent/gallery-2.html",
    ],
    galleryCaptions: [
      "Knowing when to stop: the agent hands the chat to a person with a summary, beside the skills, tone and store connection set for that business.",
      "One agent answering in English, Bangla and Banglish, each reply backed by the tool it called.",
    ],
    // home card: the product photo inside the chat mock
    cardScreens: ["/work/errum-online-store/p/signature-obsidian.webp"],
    stats: [
      { number: "3 Languages", label: "Bangla, English & Banglish", description: "Understands and replies naturally across all three." },
      { number: "Configurable", label: "Per-Client Setup", description: "Skills and tone are configured per business — no rebuild needed." },
      { number: "1 Connection", label: "Store Integration", description: "Works with WooCommerce, Shopify or a custom store." },
    ],
    relatedSlugs: ["version3-omni-channel", "adlead-analyzer"],
  },
  {
    slug: "listenr-for-bkash",
    title: "ListenR for bKash",
    tagline: "Catch the post before it becomes a crisis.",
    tagPill: "Client Project",
    cardImage: "/work/listenr-for-bkash/hero.html",
    heroImage: "/work/listenr-for-bkash/hero.html",
    thumbnail: "/work/listenr-for-bkash/hero.html",
    year: "2026",
    timeline: "Feb 2026 – ongoing",
    services: ["Social Listening", "Brand Safety Monitoring"],
    industry: "Financial Services · Mobile Financial Services · Brand & Marketing Communications",
    client: "bKash — mobile financial services provider, Bangladesh. Delivered for the marketing team.",
    status: "Delivered for the bKash marketing team",
    liveUrl: null,
    challenge:
      "A brand the size of bKash gets mentioned across thousands of Facebook pages, groups and comment threads every day. The marketing team learned about damaging posts — scam claims, fake promotions, angry threads — only when someone happened to scroll past one, by which point it had already spread.",
    solution:
      "A brand-safety monitor that watches Facebook pages, groups and searches for posts about the brand, reads them — images and comments included — and pushes an alert the moment something damaging appears. It reads the picture: most damaging posts about a financial brand are screenshots and poster graphics with no searchable text in them at all, and ListenR looks at the image itself, so it catches what keyword monitoring walks straight past.",
    results:
      "Reputation monitoring moved from someone noticing a post to an alert landing on a phone. Flagged posts arrive already sorted by severity with the comment mood already summarised, so the team triages instead of reads.",
    galleryImages: [
      "/work/listenr-for-bkash/gallery-1.html",
      "/work/listenr-for-bkash/gallery-2.html",
    ],
    galleryCaptions: [
      "Keyword monitoring misses a scam post whose caption never names the brand. ListenR reads the image and flags it.",
      "The alert on a marketing team phone, opened straight into the post and marked resolved.",
    ],
    // home card scope (4:3): the image-reading comparison
    cardScreens: ["/work/listenr-for-bkash/gallery-1.html"],
    stats: [
      { number: "24/7", label: "Automated Scanning", description: "Watches pages, groups and keyword searches around the clock." },
      { number: "Image-Aware", label: "Reads Inside Screenshots", description: "Flags damaging posts even when the only content is inside an image." },
      { number: "Real-Time", label: "Push Alerts", description: "Notifications land on phone and desktop the moment something is flagged." },
    ],
    relatedSlugs: ["adlead-analyzer", "rahiee-ai"],
  },
  {
    slug: "adlead-analyzer",
    title: "AdLead Analyzer",
    tagline: "See every ad your competitors are running.",
    tagPill: "Internal Tool",
    cardImage: "/work/adlead-analyzer/hero.html",
    heroImage: "/work/adlead-analyzer/hero.html",
    thumbnail: "/work/adlead-analyzer/hero.html",
    year: "2026",
    timeline: "May 2026 – Jun 2026 (~6 weeks)",
    services: ["Market Intelligence", "Lead Generation"],
    industry: "Marketing & Advertising · Sales Intelligence",
    client: "Version3 (internal tool)",
    status: "Live — internal use",
    liveUrl: null,
    challenge:
      "Finding out who is actively advertising in a market meant scrolling the Facebook Ad Library by hand and copying results into a spreadsheet. Commercial tools charge per seat and per query, cap exports, and still cover the Bangladeshi market poorly.",
    solution:
      "A market intelligence tool that tracks which brands are advertising on Facebook, what they are running, and which pages are worth pursuing as leads — with results exportable or piped straight into automated workflows. It runs as a step inside automated workflows rather than as a dashboard someone has to remember to open, and being self-hosted with no paid data provider, a research run costs nothing and stays private.",
    results:
      "Replaced manual Ad Library research and per-seat commercial tooling. Keyword research that previously took hours of scrolling now runs automatically and surfaces advertiser pages worth following up. Wired directly into Version3's automation workflows.",
    galleryImages: [
      "/work/adlead-analyzer/gallery-1.html",
      "/work/adlead-analyzer/gallery-2.html",
    ],
    galleryCaptions: [
      "One advertiser in depth: active and inactive ads, where the ads send people, and a relevance score.",
      "AdLead as a step in an n8n workflow: a daily run, business pages only, rows added to a sheet and the sales channel told.",
    ],
    // home card: keyword in, scored advertisers out, in one strip
    cardScreens: ["/work/adlead-analyzer/card-flow.html"],
    stats: [
      { number: "2-Stage", label: "Research Workflow", description: "Find advertisers by keyword, then analyse each page in depth." },
      { number: "CSV / JSON", label: "Exportable Results", description: "Results export directly or pipe into automation tools like n8n." },
      { number: "Self-Hosted", label: "No Per-Seat Cost", description: "No paid data provider — research stays private and free to run." },
    ],
    relatedSlugs: ["version3-ai-support-agent", "listenr-for-bkash"],
  },
  {
    slug: "version3-website-hiring-suite",
    title: "Version3 Website & Hiring Suite",
    tagline: "A marketing site with a self-learning recruiter behind it.",
    tagPill: "Own Product · Internal",
    cardImage: "/work/version3-website-hiring-suite/hero.html",
    heroImage: "/work/version3-website-hiring-suite/hero.html",
    thumbnail: "/work/version3-website-hiring-suite/hero.html",
    year: "2026",
    timeline: "May 2026 – Jul 2026 (~2 months)",
    services: ["Marketing Site", "HR Platform"],
    industry: "Software / Technology · HR & Recruitment",
    client: "Version3 (own product)",
    status: "Live",
    liveUrl: "https://version3.ai/careers",
    challenge:
      "Hiring ran on scattered job posts, CVs piling up in an inbox, and interview scheduling that collapsed into a dozen email threads per candidate. There was no consistent bar between reviewers and no record of why anyone was rejected.",
    solution:
      "Version3's public website and careers portal, combined with a full HR panel: job postings, applicant tracking, AI CV grading that learns from your own past hires, interview scheduling, and a self-service booking portal for candidates. The AI grader learns — every candidate the team shortlists becomes a reference point fed into future scoring, so the bar becomes specific to how this company actually hires and gets sharper with every round, with no manual tuning.",
    results:
      "Replaced an inbox-and-spreadsheet process with one system covering the full funnel — post, apply, score, shortlist, schedule, decide. CV screening went from hours of reading to minutes. Interview coordination went from email threads to candidates booking themselves. Email and scheduling cost nothing to run.",
    galleryImages: [
      "/work/version3-website-hiring-suite/gallery-1.html",
      "/work/version3-website-hiring-suite/gallery-2.html",
    ],
    galleryCaptions: [
      "The public careers page on version3.ai and its application form.",
      "Candidates book their own interview slot, and the HR slot grid updates as they do.",
    ],
    stats: [
      { number: "8 Stages", label: "Applicant Pipeline", description: "Full tracking from application through to decision." },
      { number: "Self-Learning", label: "AI CV Grading", description: "Scoring sharpens with every shortlisted candidate — no manual tuning." },
      { number: "Self-Service", label: "Interview Booking", description: "Candidates book and reschedule their own interview slots." },
    ],
    relatedSlugs: ["version3-omni-channel", "version3-ai-support-agent"],
  },
  {
    slug: "rahiee-ai",
    title: "Rahiee.AI",
    tagline: "Attendance that cannot be faked.",
    tagPill: "Client Project",
    cardImage: "/work/rahiee-ai/hero.html",
    heroImage: "/work/rahiee-ai/hero.html",
    thumbnail: "/work/rahiee-ai/hero.html",
    year: "2025–2026",
    timeline: "Nov 2025 – Apr 2026 (published to the App Store, April 2026)",
    services: ["Mobile HR", "Workforce Management App"],
    industry: "HR & Workforce Management · Business Services",
    client: "TanAiNent Global UK Ltd — digital solutions company, United Kingdom",
    status: "Live — published on the App Store",
    liveUrl: "https://apps.apple.com/us/app/rahiee-ai/id6755239992",
    liveLabel: "Open App Store",
    challenge:
      "Attendance was a register someone signed, a spreadsheet someone retyped, and a manager's word that a shift was worked. Staff could clock in for each other or from home, hours got argued over at payroll, and answering \"who was late this month\" meant rebuilding the month by hand.",
    solution:
      "A mobile HR app that retires the attendance register — staff clock in with a photo at the work site, the app confirms it is really them and really there, and managers get live attendance, schedules and reports on their phone. A check-in has to pass three tests at once — the right face, the right uniform, inside the right boundary — so the two ways attendance is normally faked, clocking in for a colleague and clocking in from home, simply stop working.",
    results:
      "The paper register and the spreadsheet behind it are gone; hours arrive already calculated and already verified. Managers see today's check-ins, late arrivals and pending approvals on opening the app instead of chasing them.",
    galleryImages: [
      "/work/rahiee-ai/gallery-1.html",
      "/work/rahiee-ai/gallery-2.html",
    ],
    galleryCaptions: [
      "Check-in: the right face, the right uniform and the right place, verified in one step.",
      "The manager's view: today's attendance at a glance and shift swaps approved from the phone.",
    ],
    // home card: the check-in screen inside the drawn phone
    cardScreens: ["/work/rahiee-ai/card.html"],
    stats: [
      { number: "3-Point", label: "Verified Check-In", description: "Right face, right uniform, inside the right location — all checked at once." },
      { number: "iOS", label: "App Store App", description: "Published on the App Store, so staff install it like any other app." },
      { number: "Automatic", label: "Hours Calculation", description: "Working hours calculated automatically from verified clock-ins." },
    ],
    relatedSlugs: ["errum-online-store", "listenr-for-bkash"],
  },
];

/** True when a study carries a real client quote (add `testimonial: { quote, name, role, avatar }`). */
export const hasTestimonial = (t) => Boolean(t?.quote) && !t.quote.trim().startsWith("[");

export function getCaseStudyBySlug(slug) {
  return caseStudies.find((study) => study.slug === slug);
}

export function getOtherCaseStudies(currentSlug, count = 2) {
  const current = getCaseStudyBySlug(currentSlug);

  if (current?.relatedSlugs?.length) {
    const related = current.relatedSlugs
      .map((slug) => getCaseStudyBySlug(slug))
      .filter(Boolean);
    if (related.length) return related.slice(0, count);
  }

  return caseStudies.filter((study) => study.slug !== currentSlug).slice(0, count);
}
